import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Check, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const EditablePostTab = ({ event, onUpdate }) => {
  const [notes, setNotes] = useState(event?.post_event_notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isInitialMount = useRef(true);
  const pendingChanges = useRef({});
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (isInitialMount.current) {
      setNotes(event?.post_event_notes || '');
      isInitialMount.current = false;
    }
  }, [event?.post_event_notes]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save post event notes:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  const queueChange = useCallback((changes) => {
    pendingChanges.current = { ...pendingChanges.current, ...changes };
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  const saveNow = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    doSave();
  }, [doSave]);

  const handleChange = (value) => {
    setNotes(value);
    queueChange({ post_event_notes: value });
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-teal-400" />
            Post Event
          </CardTitle>
          <div className="flex items-center gap-2">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Post Event Notes / Lessons Learned
          </Label>
          <Textarea
            value={notes}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Enter post event notes, lessons learned, and takeaways..."
            rows={12}
            className="bg-background/50 resize-y min-h-[200px]"
            data-testid="post-event-notes-textarea"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EditablePostTab;
